// components
import {
  ControlledAutoComplete,
  ControlledPlanUnitSelector
} from 'components/shared/ControlledFields';
import {
  GridCompact12,
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';
import { StyledAddRowMotionBoxNoPadding } from './FlexMatchFilters.styles';

// constants
import {
  FILTER_CACHE_TIME,
  PREFERENCE_OPTIONS,
  STATUS_OPTIONS
} from './FlexMatchFilters.consts';
import { FILTER_POPOVER_WIDTH } from './FilterPopper.consts';

// hooks
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'hooks';
import useGetEmployeeList from 'components/flex-portal/FlexHistory/hooks/useGetEmployeeList';
import useGetEmploymentAgencies from 'components/flex-portal/FlexHistory/hooks/useGetEmploymentAgencies';
import useGetRequestReason from 'components/flex-portal/FlexHistory/hooks/useGetRequestReason';
import { useGetPoolOptions } from 'components/admin-portal/Tables/Settings/PlanUnits/components/utils/useGetPoolOptions';
import useGetRosterFunctions from 'hooks/useGetRosterFunctions';

// queries & mutations
import { useGetClusters } from 'queries';

// types
import type { RootState } from 'redux/rootReducer';
import type { FlexMatchFilterFields } from './FlexMatchFilters.types';

// utils
import { AnimatePresence } from 'framer-motion';

export const FlexMatchFilters = ({
  showMoreFilters = false,
  startDate,
  endDate
}: {
  showMoreFilters?: boolean;
  startDate: string;
  endDate: string;
}) => {
  'use memo';

  const { t } = useTranslation('common');
  const { control } = useFormContext<FlexMatchFilterFields>();
  const hasPoolsOrgGrant = useSelector(
    (state: RootState) => state?.grants?.hasPoolsOrgGrant
  );
  const hasClustersOrgGrant = useSelector(
    (state: RootState) => state?.grants?.hasClustersOrgGrant
  );

  const { data: clustersData } = useGetClusters(FILTER_CACHE_TIME);
  const { data: poolsData } = useGetPoolOptions(FILTER_CACHE_TIME);

  const clusters = hasClustersOrgGrant ? clustersData : [];
  const pools = hasPoolsOrgGrant ? poolsData : [];

  const { data: employeeListData } = useGetEmployeeList(FILTER_CACHE_TIME);
  const { data: agencyListData } = useGetEmploymentAgencies(
    {
      startDate,
      endDate
    },
    FILTER_CACHE_TIME
  );

  const employeeList = employeeListData?.sort((a, b) =>
    a.nameInformal.localeCompare(b.nameInformal, 'en', {
      sensitivity: 'base'
    })
  );

  const { data: requestReasonList } = useGetRequestReason(FILTER_CACHE_TIME);
  const { data: rosterFunctionOptions } =
    useGetRosterFunctions(FILTER_CACHE_TIME);

  return (
    <GridContainer spacing={3} maxWidth={FILTER_POPOVER_WIDTH}>
      <GridCompact12>
        <ControlledPlanUnitSelector
          name="costCenter"
          control={control}
          variant="outlined"
          doNotPersist
          fullWidth
        />
      </GridCompact12>
      <GridCompact6>
        <ControlledAutoComplete
          disableSorting
          optionLabelProp="nameInformal"
          idProperty="employee"
          variant="outlined"
          control={control}
          name="employee"
          label={t('flexHistory.employee')}
          options={employeeList ?? []}
          fullWidth
        />
      </GridCompact6>
      <GridCompact6>
        <ControlledAutoComplete
          disableSorting
          optionLabelProp="description"
          idProperty="id"
          variant="outlined"
          control={control}
          name="rosterFunction"
          label={t('flexHistory.requestFunction')}
          options={rosterFunctionOptions ?? []}
          fullWidth
        />
      </GridCompact6>
      <AnimatePresence initial={false}>
        {showMoreFilters && (
          <>
            <StyledAddRowMotionBoxNoPadding key="motion-show-more-filters">
              <GridCompact6>
                <ControlledAutoComplete
                  disableSorting
                  optionLabelProp="name"
                  idProperty="id"
                  variant="outlined"
                  control={control}
                  name="reason"
                  label={t('flexHistory.requestReason')}
                  options={requestReasonList ?? []}
                  fullWidth
                />
              </GridCompact6>
              {hasClustersOrgGrant && (
                <GridCompact6>
                  <ControlledAutoComplete
                    disableSorting
                    optionLabelProp="name"
                    idProperty="clusterId"
                    variant="outlined"
                    control={control}
                    name="cluster"
                    label={t('cluster')}
                    options={clusters ?? []}
                    fullWidth
                  />
                </GridCompact6>
              )}
              {hasPoolsOrgGrant && (
                <GridCompact6>
                  <ControlledAutoComplete
                    disableSorting
                    optionLabelProp="label"
                    idProperty="value"
                    variant="outlined"
                    control={control}
                    name="pool"
                    label={t('Pools')}
                    options={pools ?? []}
                    fullWidth
                  />
                </GridCompact6>
              )}
            </StyledAddRowMotionBoxNoPadding>
            <StyledAddRowMotionBoxNoPadding>
              <GridCompact6>
                <ControlledAutoComplete
                  disableSorting
                  optionLabelProp="name"
                  idProperty="id"
                  variant="outlined"
                  control={control}
                  name="agency"
                  label={t('flexMatch.employmentAgencies')}
                  options={agencyListData ?? []}
                  fullWidth
                />
              </GridCompact6>

              <GridCompact6>
                <ControlledAutoComplete
                  disableSorting
                  optionLabelProp="label"
                  idProperty="value"
                  variant="outlined"
                  control={control}
                  name="preference"
                  label={t('flexMatch.preference')}
                  options={PREFERENCE_OPTIONS}
                  fullWidth
                />
              </GridCompact6>

              <GridCompact6>
                <ControlledAutoComplete
                  disableSorting
                  optionLabelProp="label"
                  idProperty="id"
                  variant="outlined"
                  control={control}
                  name="status"
                  label={t('status')}
                  options={STATUS_OPTIONS}
                  fullWidth
                />
              </GridCompact6>
            </StyledAddRowMotionBoxNoPadding>
          </>
        )}
      </AnimatePresence>
    </GridContainer>
  );
};
